import * as R from "ramda";
import { ACTION_TYPE } from "../../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../../core/redux/api/circuitBreaker/module";
import { apiGetGuestHydrationErrors } from "../../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  getLoading,
  removeLoadingAndError,
} from "../../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../../core/redux/IAppState";
import { assoc, isNilOrEmpty } from "../../language/module";
import { getResource } from "../../language/resources/getResource";
import { GuestHydrationErrorReportData } from "../models";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from "./constants";

export const KEY_ACTION = ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_REQUEST;
export const ACTIONS = [
  ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_REQUEST,
  ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_RESPONSE,
  ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_ERROR,
];
/**
 * An action to dispatch to get a paginated list guest hydration errors for the specified resort
 */
export const getGuestHydrationErrorsAction = (
  resortAbbrev: string,
  days: number,
  pageSize?: number | null,
  pageNumber?: number | null,    /* First page number = 1 (NOT 0 based) */
  masterGuestId?: string | null
) =>
  makeApiAction({
    endpoint: apiGetGuestHydrationErrors(
      resortAbbrev,
      days,
      pageSize ?? DEFAULT_PAGE_SIZE,
      pageNumber ?? DEFAULT_PAGE_NUMBER,
      masterGuestId ?? null
    ),
    method: "GET",
    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),
    types: ACTIONS.map((action) => ({
      type: action,
      meta: { resortAbbrev, pageSize, days },
    })) as RsaaTypes,
    // Bail if already loading results for that query
    bailout: (state: IAppState) =>
      hasTrippedBreaker(state, KEY_ACTION) ||
      (R.path(["selectedResort", "resortAbbreviation"], state) ===
        resortAbbrev &&
        !!getLoading(state, KEY_ACTION, KEY_ACTION)),
  });

export const reducer: ApiReducer<GuestHydrationErrorReportData> = (
  state,
  action,
  error,
  isLoading,
  payload
) => {
  const pageSize = action.meta.pageSize || DEFAULT_PAGE_SIZE;
  const resortAbbrev = action.meta.resortAbbrev;
  const appStateResort = R.path<string>(
    ["selectedResort", "resortAbbreviation"],
    state
  );

  // Ignore the results if the response is for a previous query that didn't complete before the
  // user switched resorts
  if (
    action.type === ACTION_TYPE.GET_GUEST_HYDRATION_ERRORS_RESPONSE &&
    !isNilOrEmpty(appStateResort) &&
    appStateResort !== resortAbbrev
  ) {
    return state;
  }

  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      KEY_ACTION,
      KEY_ACTION,
      error ??
        getResource(state.locale, "GET_GUEST_HYDRATION_ERRORS_ACTION_ERROR")
    );
  }

  if (!payload) {
    payload = {
      totalCount: 0,
      guestCount: 0,
      pageSize: pageSize,
      isLastPage: true,
      guestHydrationErrorDetails: [],
    };
  }

  const noLoadingOrError = removeLoadingAndError(
    withBreaker,
    KEY_ACTION,
    KEY_ACTION
  );

  return assoc("guestHydrationErrorReport", payload, noLoadingOrError);
};

export const getGuestHydrationErrorsReducer =
  makeApiReducer<GuestHydrationErrorReportData>(ACTIONS, KEY_ACTION, reducer);
